import { Encomenda, EncomendaStatus } from '../services/encomendaApi';

/** The sortable columns, shared by the Encomendas page and the Operational panel. */
export type EncomendaSortKey =
  | 'destino'
  | 'transportadora'
  | 'rastreio'
  | 'status'
  | 'recebida'
  | 'retirada';

export type EncomendaSortDir = 'asc' | 'desc';

export interface EncomendaSort {
  key: EncomendaSortKey;
  dir: EncomendaSortDir;
}

/**
 * Newest arrival first — what the list has always shown.
 *
 * Adding sorting must not silently reorder the screen everybody already works from, so the
 * default is the existing order rather than, say, the first column.
 */
export const DEFAULT_ENCOMENDA_SORT: EncomendaSort = { key: 'recebida', dir: 'desc' };

/** Date columns open at "most recent first"; text columns at A→Z. */
const DATE_KEYS: EncomendaSortKey[] = ['recebida', 'retirada'];

/**
 * When the package was actually collected.
 *
 * Only a pickup counts: a devolvida package was never retirada, and showing its return time under
 * a "Retirada" heading would be a quiet lie about what happened to it.
 */
export function pickupTime(encomenda: Pick<Encomenda, 'status' | 'picked_up_at'>): string | undefined {
  return encomenda.status === 'picked_up' ? encomenda.picked_up_at || undefined : undefined;
}

/** The sort produced by clicking `key` while `current` is in effect. */
export function nextEncomendaSort(current: EncomendaSort, key: EncomendaSortKey): EncomendaSort {
  if (current.key === key) {
    return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: DATE_KEYS.includes(key) ? 'desc' : 'asc' };
}

/** A package's progress, so Status sorts along its life instead of alphabetically. */
const STATUS_ORDER: Record<EncomendaStatus, number> = {
  received: 0,
  notified: 1,
  picked_up: 2,
  returned: 3,
};

// Accent- and number-aware, so "Álvaro" sits with "Alvaro" and UNIDADES / 7 comes before
// UNIDADES / 021 — plain string order gets both wrong on this data.
const collator = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

type SortableEncomenda = Pick<
  Encomenda,
  'id' | 'status' | 'created_at' | 'picked_up_at' | 'returned_at' | 'transportadora' | 'tracking_code'
>;

/**
 * Sorts a copy of `items`.
 *
 * `destino` reads through `label` because the two call sites name that column differently — the
 * condomínio page shows "Unidade / Morador", an empresa shows "Departamento / Funcionário" — and
 * the sort has to follow whatever the row actually displays.
 *
 * Empty values always sink to the bottom, in BOTH directions: sorting by Retirada to see who
 * collected last must not open with the forty packages still waiting. Ties fall back to arrival
 * time so the order is stable and never jitters between renders.
 */
export function sortEncomendas<T extends SortableEncomenda>(
  items: T[],
  sort: EncomendaSort,
  label: (encomenda: T) => string
): T[] {
  const direction = sort.dir === 'asc' ? 1 : -1;

  const compare = (a: T, b: T): number => {
    switch (sort.key) {
      case 'destino':
        return collator.compare(label(a), label(b));
      case 'transportadora':
        return text(a.transportadora, b.transportadora);
      case 'rastreio':
        return text(a.tracking_code, b.tracking_code);
      case 'status':
        return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      case 'recebida':
        return time(a.created_at) - time(b.created_at);
      case 'retirada':
        return time(pickupTime(a)) - time(pickupTime(b));
      default:
        return 0;
    }
  };

  // Blank-vs-blank and blank-vs-value, decided before direction is applied so emptiness always
  // sinks rather than flipping to the top on a descending sort.
  const emptiness = (encomenda: T): boolean => {
    switch (sort.key) {
      case 'transportadora':
        return !encomenda.transportadora;
      case 'rastreio':
        return !encomenda.tracking_code;
      case 'retirada':
        return !pickupTime(encomenda);
      case 'destino':
        return !label(encomenda).trim();
      default:
        return false;
    }
  };

  return items.slice().sort((a, b) => {
    const aEmpty = emptiness(a);
    const bEmpty = emptiness(b);
    if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;

    const primary = compare(a, b);
    if (primary !== 0) return primary * direction;

    // Stable tiebreaker: newest arrival first, then id so equal timestamps never swap.
    const byArrival = time(b.created_at) - time(a.created_at);
    return byArrival !== 0 ? byArrival : String(a.id).localeCompare(String(b.id));
  });
}

function text(a?: string | null, b?: string | null): number {
  return collator.compare(a || '', b || '');
}

function time(value?: string | null): number {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}
